'use client';

import React from 'react';
import { m } from 'framer-motion';
import { Plug, Zap } from 'lucide-react';
import Image from 'next/image';

// Real integration logos from known CDNs / brand kits
const integrations = [
    { name: 'Meta Ads', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/meta.svg', category: 'Paid Ads' },
    { name: 'Google Ads', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/googleads.svg', category: 'Paid Ads' },
    { name: 'TikTok', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/tiktok.svg', category: 'Social' },
    { name: 'Instagram', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/instagram.svg', category: 'Social' },
    { name: 'LinkedIn', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/linkedin.svg', category: 'Social' },
    { name: 'Twitter / X', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/x.svg', category: 'Social' },
    { name: 'WhatsApp', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/whatsapp.svg', category: 'Messaging' },
    { name: 'Shopify', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/shopify.svg', category: 'E-Commerce' },
    { name: 'Stripe', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/stripe.svg', category: 'Payments' },
    { name: 'HubSpot', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/hubspot.svg', category: 'CRM' },
    { name: 'Notion', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/notion.svg', category: 'Productivity' },
    { name: 'Slack', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/slack.svg', category: 'Communication' },
    { name: 'Airtable', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/airtable.svg', category: 'CRM' },
    { name: 'Calendly', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/calendly.svg', category: 'Scheduling' },
    { name: 'SendGrid', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/sendgrid.svg', category: 'Email' },
    { name: 'WooCommerce', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/woocommerce.svg', category: 'E-Commerce' },
    { name: 'YouTube', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/youtube.svg', category: 'Social' },
    { name: 'Reddit', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/reddit.svg', category: 'Social' },
    { name: 'Discord', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/discord.svg', category: 'Communication' },
    { name: 'Twilio', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/twilio.svg', category: 'Messaging' },
    { name: 'Google Analytics', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/googleanalytics.svg', category: 'Analytics' },
    { name: 'Mixpanel', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/mixpanel.svg', category: 'Analytics' },
    { name: 'Zapier', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/zapier.svg', category: 'Automation' },
    { name: 'OpenAI', logo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@develop/icons/openai.svg', category: 'AI' },
];

// Split for two marquee rows
const row1 = integrations.slice(0, 12);
const row2 = integrations.slice(12);

const IntegrationLogo = ({ item }: { item: typeof integrations[0] }) => (
    <div className="flex items-center gap-3 px-5 py-3 bg-white border border-neutral-200 rounded-xl shadow-sm hover:border-neutral-300 hover:shadow-md transition-all shrink-0 group cursor-default select-none">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
            src={item.logo}
            alt={item.name}
            width={18}
            height={18}
            loading="lazy"
            decoding="async"
            className="w-[18px] h-[18px] grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all"
            onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
        />
        <span className="text-sm font-semibold text-neutral-600 whitespace-nowrap">{item.name}</span>
    </div>
);

const MarqueeRow = ({ items, reverse = false }: { items: typeof integrations; reverse?: boolean }) => (
    <div className="flex overflow-hidden py-2 select-none">
        <m.div
            animate={{ x: reverse ? ['-50%', '0%'] : ['0%', '-50%'] }}
            transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
            className="flex gap-3 shrink-0"
        >
            {[...items, ...items].map((item, i) => (
                <IntegrationLogo key={`${item.name}-${i}`} item={item} />
            ))}
        </m.div>
    </div>
);

export const Integrations = () => {
    return (
        <section className="py-24 bg-[#FAFAFA] border-b border-neutral-200 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 mb-14">
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center max-w-3xl mx-auto"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-neutral-200 text-neutral-700 text-xs font-semibold mb-8 uppercase tracking-widest shadow-sm">
                        <Plug size={13} /> 100+ Integrations
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-6 tracking-tight">
                        Connected to your entire stack.
                    </h2>
                    <p className="text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto">
                        Powered by Composio, Axist connects to 100+ tools out of the box — from your ad platforms to your CRM and beyond.
                    </p>
                </m.div>
            </div>

            {/* Marquee rows */}
            <div className="space-y-3">
                <MarqueeRow items={row1} />
                <MarqueeRow items={row2} reverse />
            </div>

            {/* Stats bar */}
            <m.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="max-w-4xl mx-auto px-6 mt-14"
            >
                <div className="grid grid-cols-3 gap-4">
                    {[
                        { val: '100+', label: 'Integrations', icon: Plug },
                        { val: '1-click', label: 'OAuth setup', icon: Zap },
                        { val: '24/7', label: 'Data sync', icon: Zap },
                    ].map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <div key={stat.label} className="flex flex-col items-center gap-2 py-6 px-4 bg-white border border-neutral-200 rounded-2xl shadow-sm text-center hover:border-neutral-300 transition-colors">
                                <Icon size={18} className="text-neutral-400" />
                                <p className="text-2xl font-black text-neutral-900 tracking-tight">{stat.val}</p>
                                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">{stat.label}</p>
                            </div>
                        );
                    })}
                </div>
            </m.div>
        </section>
    );
};
